import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalFooter, ModalHeader, ModalBody } from 'reactstrap';
import PageBreadcrumb from '../../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import { confirmDelete } from '../../../utils/confirm';
import notify from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import ActionButton from '../../../Components/Common/ActionButton';
import {
  CheckField,
  FormGrid,
  RequiredNote,
  SelectField,
  TextField,
  focusFirstError,
  hasErrors,
  required,
  validateForm as runValidation,
} from '../../../Components/Form';
import ListPager from '../../../Components/Common/ListPager';
import {
  EmptyState,
  SkeletonRows,
} from '../../../Components/Common/ListStates';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../../Components/Common/ListFilters';
import '../../TenderOpening/components/TenderOpening.css';
import './MenuView.css';
import StatusBadge from '../../../Components/Common/StatusBadge';

const MENU_SCHEMA = {
  code: { label: 'Code', rules: [required()] },
  label: { label: 'Label', rules: [required()] },
};
const MENU_FIELD_ORDER = ['code', 'label'];
const STATUS_OPTIONS = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const MenuManagement = () => {
  const token = getAuthToken();
  const [data, setData] = useState({ items: [] });
  const [parentOptions, setParentOptions] = useState([]);
  const [modal, setModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Table States
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [sortField, setSortField] = useState('orderNumber');
  const [sortDirection, setSortDirection] = useState('asc');
  const [statusFilter, setStatusFilter] = useState('');
  const [parentFilter, setParentFilter] = useState('');

  const initialFormState = {
    id: null,
    code: '',
    label: '',
    icon: '',
    isActive: true,
    isHeader: false,
    link: '',
    orderNumber: 0,
    parentId: null,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchParentMenus = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(
        `${api.API_URL}/menu?PageSize=1000&IsActive=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const result = await response.json();
      if (result.data?.items) {
        setParentOptions(
          result.data.items.map(item => ({
            label: item.label,
            value: item.id,
          }))
        );
      }
    } catch (error) {
      console.error('Failed fetching parent menus', error);
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const queryParams = {
        PageNumber: currentPage.toString(),
        PageSize: rowsPerPage.toString(),
      };

      const qs = new URLSearchParams(queryParams);

      if (debouncedSearchText && debouncedSearchText.trim() !== '') {
        qs.append('Search', debouncedSearchText.trim());
      }

      if (statusFilter) {
        qs.append('IsActive', statusFilter);
      }

      if (parentFilter) {
        qs.append('ParentId', parentFilter);
      }

      if (sortField) {
        qs.append('SortBy', sortField);
        qs.append('IsDescending', sortDirection === 'desc' ? 'true' : 'false');
      }

      const response = await fetch(`${api.API_URL}/menu?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      setData({ items: result.data?.items || [] });
      setTotalRows(result.data?.totalItems || 0);
    } catch (error) {
      notify.apiError(error, 'load data');
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    token,
    debouncedSearchText,
    statusFilter,
    parentFilter,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchParentMenus();
  }, [token]);

  const handleSearch = e => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = value => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleParentFilter = value => {
    setParentFilter(value);
    setCurrentPage(1);
  };

  const clearFilter = () => {
    setSearchText('');
    setDebouncedSearchText('');
    setStatusFilter('');
    setParentFilter('');
    setCurrentPage(1);
  };

  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: () => handleSearch({ target: { value: '' } }),
    },
    statusFilter && {
      key: 'status',
      label: 'Status',
      value:
        STATUS_OPTIONS.find(o => o.value === statusFilter)?.label ??
        statusFilter,
      onRemove: () => handleStatusFilter(''),
    },
    parentFilter && {
      key: 'parent',
      label: 'Parent',
      value:
        parentOptions.find(o => o.value === parentFilter)?.label ??
        parentFilter,
      onRemove: () => handleParentFilter(''),
    },
  ].filter(Boolean);

  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const toggleModal = () => {
    if (modal) {
      // setFormData(initialFormState);
      setErrors({});
    }
    setModal(!modal);
  };

  const handleEdit = row => {
    setFormData({
      id: row.id,
      code: row.code || '',
      label: row.label || '',
      icon: row.icon || '',
      isActive: row.isActive ?? true,
      isHeader: row.isHeader ?? false,
      link: row.link || '',
      orderNumber: row.orderNumber || 0,
      parentId: row.parentId || null,
    });
    setModal(true);
  };

  const handleDelete = async (id, label) => {
    const confirmed = await confirmDelete('menu', label);

    if (confirmed) {
      try {
        const response = await fetch(`${api.API_URL}/menu/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Delete failed');
        notify.success('Menu deleted successfully.');
        fetchData();
      } catch (error) {
        notify.apiError(error, 'delete data');
      }
    }
  };

  const validateForm = () => {
    const tempErrors = runValidation(formData, MENU_SCHEMA);
    setErrors(tempErrors);
    if (!hasErrors(tempErrors)) return true;
    focusFirstError(tempErrors, MENU_FIELD_ORDER);
    return false;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setModalLoading(true);

    try {
      const payload = { ...formData };
      const isEdit = !!formData.id;
      const url = isEdit
        ? `${api.API_URL}/menu/${formData.id}`
        : `${api.API_URL}/menu`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Save operation failed');

      notify.success(`Menu ${isEdit ? 'updated' : 'created'} successfully`);
      toggleModal();
      fetchData();
      fetchParentMenus();
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      setModalLoading(false);
    }
  };

  const columns = [
    {
      id: 'orderNumber',
      name: 'Order',
      selector: row => row.orderNumber,
      sortable: true,
      width: '90px',
      cell: row => <span className='fw-medium'>{row.orderNumber}</span>,
    },
    {
      id: 'code',
      name: 'Code',
      selector: row => row.code,
      sortable: true,
      minWidth: '150px',
      cell: row => (
        <span className='to-title' title={String(row.code ?? '')}>
          {row.code ?? '-'}
        </span>
      ),
    },
    {
      id: 'label',
      name: 'Label',
      selector: row => row.label,
      sortable: true,
      minWidth: '200px',
      cell: row => (
        <div className='d-flex align-items-center gap-2'>
          {row.icon && <i className={`${row.icon} text-muted`}></i>}
          <span>{row.label}</span>
          {row.isHeader && <StatusBadge tone='info' label='Header' />}
        </div>
      ),
    },
    {
      name: 'Link',
      selector: row => row.link,
      minWidth: '200px',
      cell: row => <span className='text-muted'>{row.link || '-'}</span>,
    },
    {
      name: 'Status',
      selector: row => row.isActive,
      width: '120px',
      center: true,
      cell: row => (
        <StatusBadge
          tone={row.isActive ? 'success' : 'danger'}
          label={row.isActive ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      width: '110px',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleEdit(row)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDelete(row.id, row.label || row.code)}
          />
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
        <div className='email-content minimal-border'>
          <div className='p-4'>
            <div className='to-hero mb-4'>
              <PageBreadcrumb
                fallback={{
                  label: 'Menu Management',
                  to: '/apps-menumanagement/view',
                }}
              />

              <div className='d-flex align-items-center gap-3 flex-wrap'>
                <span className='to-hero-icon'>
                  <i className='ri-menu-2-line' aria-hidden='true' />
                </span>
                <div className='flex-grow-1'>
                  <h4 className='to-hero-title'>Menu Management</h4>
                  <p className='to-hero-subtitle'>
                    Maintain the application menu structure, links and order.
                  </p>
                </div>
                <div className='d-flex gap-2 flex-wrap'>
                  <button
                    type='button'
                    className='to-hero-btn'
                    onClick={() => fetchData()}
                  >
                    <i className='ri-refresh-line' aria-hidden='true' />
                    Refresh
                  </button>
                  <button
                    type='button'
                    className='to-hero-btn to-hero-btn--solid'
                    onClick={toggleModal}
                  >
                    <i className='ri-add-line' aria-hidden='true' />
                    Add Menu
                  </button>
                </div>
              </div>
            </div>

            <div className='to-panel'>
              <div className='to-toolbar'>
                <ListSearch
                  value={searchText}
                  onChange={handleSearch}
                  placeholder='Search menus...'
                  ariaLabel='Search menus'
                />
                <ListFilterSelect
                  label='Status'
                  value={statusFilter}
                  onChange={handleStatusFilter}
                  allLabel='All Status'
                  options={STATUS_OPTIONS}
                />
                <ListFilterSelect
                  label='Parent'
                  value={parentFilter}
                  onChange={handleParentFilter}
                  allLabel='All Parents'
                  options={parentOptions}
                />
                <ClearFiltersButton
                  count={filterChips.length}
                  onClick={clearFilter}
                />
                <span className='to-toolbar-meta'>
                  {isLoading
                    ? 'Loading...'
                    : `Showing ${startEntry}–${endEntry} of ${totalRows} menus`}
                </span>
              </div>

              <ActiveFilterChips chips={filterChips} />

              <div className='to-table-wrap'>
                <DataTable
                  columns={columns}
                  data={data.items}
                  onSort={(column, dir) => {
                    setSortField(column.id);
                    setSortDirection(dir);
                    setCurrentPage(1);
                  }}
                  sortServer
                  defaultSortFieldId='orderNumber'
                  defaultSortAsc
                  highlightOnHover
                  persistTableHead
                  progressPending={isLoading}
                  progressComponent={<SkeletonRows />}
                  noDataComponent={
                    <EmptyState
                      title='No menu found'
                      emptyHint='No menu items have been created yet. Menus define the sidebar navigation, so add one to make pages reachable.'
                      filteredHint={`No menu matches ${filterChips
                        .map(chip => `${chip.label} "${chip.value}"`)
                        .join(
                          ', '
                        )}. Try a different label or clear the filters.`}
                      hasFilters={filterChips.length > 0}
                      onClear={clearFilter}
                      action={{ label: 'Add Menu', onClick: toggleModal }}
                    />
                  }
                  customStyles={{
                    headCells: {
                      style: { paddingLeft: '12px', paddingRight: '12px' },
                    },
                    cells: {
                      style: {
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        wordBreak: 'break-word',
                      },
                    },
                    table: { style: { width: '100%' } },
                  }}
                />
              </div>

              <ListPager
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalRows={totalRows}
                isLoading={isLoading}
                onPageChange={setCurrentPage}
                onRowsPerPageChange={rows => {
                  setRowsPerPage(rows);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modal}
        toggle={toggleModal}
        size='lg'
        backdrop='static'
        centered
        onClosed={() => setFormData(initialFormState)}
      >
        <ModalHeader toggle={toggleModal} className='bg-light'>
          {formData.id ? 'Edit Menu' : 'Create New Menu'}
        </ModalHeader>
        <ModalBody>
          <RequiredNote />
          <FormGrid columns={2}>
            <TextField
              label='Code'
              name='code'
              required
              placeholder='e.g. MNU_USER'
              value={formData.code}
              error={errors.code}
              onChange={e => {
                setFormData(prev => ({ ...prev, code: e.target.value }));
                if (errors.code)
                  setErrors(prev => ({ ...prev, code: undefined }));
              }}
            />
            <TextField
              label='Label'
              name='label'
              required
              placeholder='e.g. User Management'
              value={formData.label}
              error={errors.label}
              onChange={e => {
                setFormData(prev => ({ ...prev, label: e.target.value }));
                if (errors.label)
                  setErrors(prev => ({ ...prev, label: undefined }));
              }}
            />
            <CheckField
              switchStyle
              label='Active'
              name='isActive'
              checked={formData.isActive}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  isActive: e.target.checked,
                }))
              }
            />
            <CheckField
              switchStyle
              label='Header label'
              name='isHeader'
              hint='A header groups other menus and has no link, icon or parent'
              checked={formData.isHeader}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  isHeader: e.target.checked,
                  parentId: e.target.checked ? null : prev.parentId,
                }))
              }
            />
            <TextField
              label='Link path'
              name='link'
              placeholder='e.g. /apps-usermanagement'
              mode={formData.isHeader ? 'disabled' : 'editable'}
              hint={
                formData.isHeader ? 'Not used for header labels' : undefined
              }
              value={formData.link || ''}
              onChange={e =>
                setFormData(prev => ({ ...prev, link: e.target.value }))
              }
            />
            <TextField
              label='Icon class'
              name='icon'
              placeholder='e.g. ri-dashboard-line'
              mode={formData.isHeader ? 'disabled' : 'editable'}
              hint={
                formData.isHeader ? 'Not used for header labels' : undefined
              }
              value={formData.icon || ''}
              onChange={e =>
                setFormData(prev => ({ ...prev, icon: e.target.value }))
              }
            />
            <SelectField
              label='Parent menu'
              name='parentId'
              options={parentOptions}
              mode={formData.isHeader ? 'disabled' : 'editable'}
              hint={
                formData.isHeader ? 'Header labels have no parent' : undefined
              }
              value={formData.parentId}
              onChange={value =>
                setFormData(prev => ({ ...prev, parentId: value || null }))
              }
            />
            <TextField
              label='Order number'
              name='orderNumber'
              type='number'
              value={formData.orderNumber}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  orderNumber: parseInt(e.target.value) || 0,
                }))
              }
            />
          </FormGrid>
        </ModalBody>
        <ModalFooter>
          <ActionButton
            variant='secondary'
            icon='ri-close-line'
            onClick={toggleModal}
            disabled={modalLoading}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant='primary'
            icon='ri-save-line'
            onClick={handleSave}
            loading={modalLoading}
          >
            {formData.id ? 'Save Changes' : 'Create Menu'}
          </ActionButton>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default MenuManagement;
